// Lightweight Markdown renderer — headings, bold/italic/code, bullet & numbered
// lists, simple pipe tables and paragraphs. Avoids adding a dependency.
import { Fragment } from "react";

function renderInline(text, keyBase) {
    // **bold**, *italic*, `code`
    const parts = [];
    let rest = text;
    let i = 0;
    const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/;
    let m;
    while ((m = re.exec(rest))) {
        if (m.index > 0) parts.push(<Fragment key={`${keyBase}-t${i++}`}>{rest.slice(0, m.index)}</Fragment>);
        if (m[2] !== undefined) parts.push(<strong key={`${keyBase}-b${i++}`}>{m[2]}</strong>);
        else if (m[3] !== undefined) parts.push(<em key={`${keyBase}-i${i++}`}>{m[3]}</em>);
        else if (m[4] !== undefined) parts.push(<code key={`${keyBase}-c${i++}`} className="px-1 py-0.5 rounded bg-[#EEF3FB] text-[#1F6FE5] text-[11px]">{m[4]}</code>);
        rest = rest.slice(m.index + m[0].length);
    }
    if (rest) parts.push(<Fragment key={`${keyBase}-t${i++}`}>{rest}</Fragment>);
    return parts;
}

export default function Markdown({ text = "", className = "" }) {
    const lines = String(text).replace(/\r\n/g, "\n").split("\n");
    const blocks = [];
    let i = 0;
    let key = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (!line.trim()) { i++; continue; }

        // Standalone image: ![alt](src)
        const img = /^!\[([^\]]*)\]\(([^)]+)\)\s*$/.exec(line.trim());
        if (img) {
            blocks.push(
                <figure key={key++} className="my-3">
                    <img src={img[2]} alt={img[1]} loading="lazy"
                        className="max-w-full rounded-lg border" style={{ borderColor: "#E1E8F2" }} />
                    {img[1] ? <figcaption className="text-[11.5px] mt-1 italic" style={{ color: "#6F6A64" }}>{img[1]}</figcaption> : null}
                </figure>
            );
            i++;
            continue;
        }

        // Headings
        const h = /^(#{1,4})\s+(.*)$/.exec(line);
        if (h) {
            const level = h[1].length;
            const sizes = { 1: "text-[18px]", 2: "text-[16px]", 3: "text-[14px]", 4: "text-[13px]" };
            blocks.push(
                <div key={key++} className={`font-bold text-[#1F1F1F] mt-3 mb-1.5 ${sizes[level]}`}>
                    {renderInline(h[2], `h${key}`)}
                </div>
            );
            i++;
            continue;
        }

        // Blockquote (> ...)
        if (/^\s*>\s?/.test(line)) {
            const quote = [];
            while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
                quote.push(lines[i].replace(/^\s*>\s?/, ""));
                i++;
            }
            blocks.push(
                <blockquote key={key++} className="my-2 pl-3 border-l-[3px] text-[12.7px] leading-relaxed italic"
                    style={{ borderColor: "var(--brand-accent, #C75A1B)", color: "#5A554F" }}>
                    {renderInline(quote.join(" "), `q${key}`)}
                </blockquote>
            );
            continue;
        }

        // Tables (lines containing pipes, with a separator row)
        if (line.includes("|") && i + 1 < lines.length && /^[\s|:-]+$/.test(lines[i + 1]) && lines[i + 1].includes("-")) {
            const header = line.split("|").map((c) => c.trim()).filter((_, idx, arr) => !(idx === 0 && _ === "") && !(idx === arr.length - 1 && _ === ""));
            i += 2;
            const rows = [];
            while (i < lines.length && lines[i].includes("|")) {
                const cells = lines[i].split("|").map((c) => c.trim());
                if (cells[0] === "") cells.shift();
                if (cells[cells.length - 1] === "") cells.pop();
                rows.push(cells);
                i++;
            }
            blocks.push(
                <div key={key++} className="my-2 overflow-x-auto rounded-lg border" style={{ borderColor: "#E1E8F2" }}>
                    <table className="w-full text-[12px]">
                        <thead className="bg-[#EEF3FB]"><tr>{header.map((c, ci) => <th key={ci} className="text-left px-3 py-1.5 font-bold text-[#1F1F1F]">{renderInline(c, `th${ci}`)}</th>)}</tr></thead>
                        <tbody>{rows.map((r, ri) => <tr key={ri} className="border-t" style={{ borderColor: "#EEF3FB" }}>{r.map((c, ci) => <td key={ci} className="px-3 py-1.5 align-top">{renderInline(c, `td${ri}-${ci}`)}</td>)}</tr>)}</tbody>
                    </table>
                </div>
            );
            continue;
        }

        // Bullet list
        if (/^\s*[-*]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
                i++;
            }
            blocks.push(
                <ul key={key++} className="list-disc pl-5 my-1.5 space-y-1 text-[12.7px] text-[#2D2D2D]">
                    {items.map((it, idx) => <li key={idx}>{renderInline(it, `li${key}-${idx}`)}</li>)}
                </ul>
            );
            continue;
        }

        // Numbered list
        if (/^\s*\d+\.\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
                i++;
            }
            blocks.push(
                <ol key={key++} className="list-decimal pl-5 my-1.5 space-y-1 text-[12.7px] text-[#2D2D2D]">
                    {items.map((it, idx) => <li key={idx}>{renderInline(it, `ol${key}-${idx}`)}</li>)}
                </ol>
            );
            continue;
        }

        // Paragraph (gather consecutive non-empty, non-special lines)
        const para = [line];
        i++;
        while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|\s*[-*]\s|\s*\d+\.\s|\s*>\s?)/.test(lines[i]) && !lines[i].includes("|")) {
            para.push(lines[i]);
            i++;
        }
        blocks.push(
            <p key={key++} className="my-1.5 text-[12.7px] leading-relaxed text-[#2D2D2D]">
                {renderInline(para.join(" "), `p${key}`)}
            </p>
        );
    }

    return <div className={className}>{blocks}</div>;
}
